import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory, { MaskableFeeSchedule } from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedContracts,
  MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam,
} from "work/_shared/masked-fields";
import WorkAgreementSchema from "work/work-agreements/_schemas/work-agreement-codec";
import { validate, WorkAgreementJSON, WorkAgreementParams } from "work/work-agreements/_schemas/work-agreement-schema";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeam from "./teams/client-team/client-team";
import VendorTeam from "./teams/vendor-team/vendor-team";

export type MaskableTeam = VendorTeam | MaskedTeam | undefined;

export default class WorkAgreement {
  public static get EXAMPLE(): WorkAgreement {
    return new WorkAgreement({
      id: Guid.generate(),
      RFPId: Guid.generate(),
      name: ProjectName.create("Test Agreement"),
      description: ProjectDescription.create("Description"),
      additionalVendors: [EntityMember.EXAMPLE],
      discount: new Percent(0.3),
      startDate: new AHDate(),
      endDate: new AHDate().add(1, "month"),
      hireDate: undefined,
      teamRestricted: new AHBoolean(false),
      conflicts: [WorkDocument.EXAMPLE],
      vendorFeeScheduleTemplateIds: [Guid.generate()],
      vendorPolicies: [WorkDocument.EXAMPLE],
      vendorTeamTemplateIds: [Guid.generate()],
      clientPolicies: [WorkDocument.EXAMPLE],
      clientFeeScheduleTemplateIds: [Guid.generate()],
      clientTeamTemplateIds: [Guid.generate()],
      archiveDate: undefined,
      conflictsCheckWaived: new AHBoolean(false),
      clientTeam: ClientTeam.EXAMPLE,
      vendorTeam: VendorTeam.EXAMPLE,
      feeSchedule: [FeeScheduleCategory.EXAMPLE],
    });
  }

  public static fromJSON(json: WorkAgreementJSON): WorkAgreement {
    const parsed = new WorkAgreementSchema().deserialize(json);
    return new WorkAgreement({ ...parsed });
  }

  public static create(params: WorkAgreementParams): WorkAgreement {
    const parsed = validate(params);
    return new WorkAgreement(parsed);
  }

  public readonly id?: Guid;
  public readonly hireDate?: AHDate;
  public readonly archiveDate?: AHDate;
  public readonly params: WorkAgreementParams;
  public readonly RFPId?: Guid;
  public readonly clientTeam?: ClientTeam | MaskedTeam;
  public readonly name?: ProjectName;
  public readonly description?: ProjectDescription;
  public readonly startDate?: AHDate;
  public readonly endDate?: AHDate;
  public readonly vendorTeam?: VendorTeam | MaskedTeam;
  public readonly teamRestricted: AHBoolean | MaskedTeam = new AHBoolean(false);
  public readonly conflictsCheckWaived: AHBoolean | MaskedContracts = new AHBoolean(false);
  public readonly discount?: Percent | MaskedDiscount;

  public get additionalVendors(): EntityMember[] {
    return [...this._additionalVendors];
  }
  public get feeSchedule(): FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule {
    return Masked.isMasked(this._feeSchedule) ? this._feeSchedule : [...this._feeSchedule];
  }
  public get clientPolicies(): WorkDocument[] | MaskedContracts {
    return Masked.isMasked(this._clientPolicies)
      ? this._clientPolicies
      : this._clientPolicies.map((doc) => doc.clone());
  }

  public get vendorPolicies(): WorkDocument[] | MaskedContracts {
    return Masked.isMasked(this._vendorPolicies)
      ? this._vendorPolicies
      : this._vendorPolicies.map((doc) => doc.clone());
  }

  public get conflicts(): WorkDocument[] | MaskedContracts {
    return Masked.isMasked(this._conflicts)
      ? this._conflicts
      : this._conflicts.map((doc) => doc.clone());
  }
  public get clientTeamTemplateIds(): Guid[] {
    return [...this._clientTeamTemplateIds];
  }
  public get vendorTeamTemplateIds(): Guid[] {
    return [...this._vendorTeamTemplateIds];
  }
  public get clientFeeScheduleTemplateIds(): Guid[] {
    return [...this._clientFeeScheduleTemplateIds];
  }
  public get vendorFeeScheduleTemplateIds(): Guid[] {
    return [...this._vendorFeeScheduleTemplateIds];
  }

  public get associatedUserIds(): Guid[] {
    const userIds: Guid[] = [];
    if (!Masked.isMasked(this.clientTeam)) {
      if (this.clientTeam?.leader?.userId) userIds.push(this.clientTeam.leader.userId);
      this.clientTeam?.members.forEach((member) => {
        if (member.userId) userIds.push(member.userId);
      });
    }
    if (!Masked.isMasked(this.vendorTeam)) {
      if (this.vendorTeam?.leader?.userId) userIds.push(this.vendorTeam.leader.userId);
      this.vendorTeam?.members.forEach((member) => {
        if (member.userId) userIds.push(member.userId);
      });
    }
    return userIds;
  }

  public get vendor(): EntityMember | MaskedTeam | undefined {
    if (Masked.isMasked(this.vendorTeam)) {
      return this.vendorTeam;
    }
    return this.vendorTeam?.leader;
  }

  public get isArchived(): boolean {
    return this.archiveDate !== undefined;
  }
  public get isHired(): boolean {
    return this.hireDate !== undefined;
  }

  public constructor(params: WorkAgreementParams) {
    this.params = params;
    this.id = params.id;
    this.hireDate = params.hireDate;
    this.archiveDate = params.archiveDate;
    this.RFPId = params.RFPId;
    this.clientTeam = params.clientTeam;
    this.name = params.name;
    this.description = params.description;
    this.startDate = params.startDate;
    this.endDate = params.endDate;
    this.vendorTeam = params.vendorTeam;
    this.teamRestricted = params.teamRestricted ?? new AHBoolean(false);
    this._additionalVendors = params.additionalVendors ? [...params.additionalVendors] : [];
    this._feeSchedule = params.feeSchedule ?? [];
    this._clientPolicies = params.clientPolicies ?? [];
    this._vendorPolicies = params.vendorPolicies ?? [];
    this.conflictsCheckWaived = params.conflictsCheckWaived ?? new AHBoolean(false);
    this._conflicts = params.conflicts ?? [];
    this.discount = params.discount;
    this._clientTeamTemplateIds = params.clientTeamTemplateIds ? [...params.clientTeamTemplateIds] : [];
    this._vendorTeamTemplateIds = params.vendorTeamTemplateIds ? [...params.vendorTeamTemplateIds] : [];
    this._clientFeeScheduleTemplateIds = params.clientFeeScheduleTemplateIds
      ? [...params.clientFeeScheduleTemplateIds]
      : [];
    this._vendorFeeScheduleTemplateIds = params.vendorFeeScheduleTemplateIds
      ? [...params.vendorFeeScheduleTemplateIds]
      : [];
  }

  public isAssociatedWithUserId(userId: Guid | undefined): boolean {
    if (!userId) return false;

    if (!Masked.isMasked(this.clientTeam) && this.clientTeam?.leader?.userId.isEqualTo(userId)) {
      return true;
    }

    if (
      !Masked.isMasked(this.vendorTeam) &&
      (this.vendorTeam?.leader?.userId.isEqualTo(userId) ||
        this.vendorTeam?.members.some((member) => member.userId.isEqualTo(userId)))
    ) {
      return true;
    }

    return false;
  }

  public clone(): WorkAgreement {
    const clonedWorkAgreement = new WorkAgreement({
      id: this.id?.clone(),
      hireDate: this.hireDate?.clone(),
      archiveDate: this.archiveDate?.clone(),
      additionalVendors: this._additionalVendors.map((vendor) => vendor.clone()),
      clientFeeScheduleTemplateIds: this._clientFeeScheduleTemplateIds.map((id) => id.clone()),
      clientTeamTemplateIds: this.clientTeamTemplateIds.map((id) => id.clone()),
      conflictsCheckWaived: this.conflictsCheckWaived.clone(),
      conflicts: this.conflicts,
      description: this.description ? this.description.clone() : undefined,
      discount: this.discount?.clone(),
      endDate: this.endDate ? this.endDate.clone() : undefined,
      feeSchedule: this.feeSchedule,
      name: this.name?.clone(),
      RFPId: this.RFPId?.clone(),
      startDate: this.startDate ? this.startDate.clone() : undefined,
      teamRestricted: this.teamRestricted.clone(),
      vendorTeam: this.vendorTeam?.clone(),
      clientTeam: this.clientTeam?.clone(),
      vendorFeeScheduleTemplateIds: this._vendorFeeScheduleTemplateIds.map((id) => id.clone()),
      clientPolicies: this.clientPolicies,
      vendorPolicies: this.vendorPolicies,
      vendorTeamTemplateIds: this.vendorTeamTemplateIds.map((id) => id.clone()),
    });

    return clonedWorkAgreement;
  }

  public toJSON(): WorkAgreementJSON {
    return new WorkAgreementSchema().serialize(this.params);
  }

  private readonly _additionalVendors: EntityMember[] = [];
  private readonly _feeSchedule: MaskableFeeSchedule = [];

  private readonly _clientPolicies: WorkDocument[] | MaskedContracts = [];
  private readonly _vendorPolicies: WorkDocument[] | MaskedContracts = [];
  private readonly _conflicts: WorkDocument[] | MaskedContracts = [];

  private readonly _clientTeamTemplateIds: Guid[] = [];
  private readonly _vendorTeamTemplateIds: Guid[] = [];
  private readonly _clientFeeScheduleTemplateIds: Guid[] = [];
  private readonly _vendorFeeScheduleTemplateIds: Guid[] = [];
}

export enum WorkAgreementFieldCategory {
  Details = "Details",
  Team = "Team",
  FeeSchedule = "Fee Schedule",
  Conflicts = "Conflicts",
  Policies = "Policies",
  Discount = "Discount",
  Invoices = "Invoices",
}
